import { Alert, Box, useTheme } from "@mui/material";
import * as React from "react";
import { Link, useParams } from "react-router-dom";

import { useAuth } from "@/providers";
import { ExpandableCopyText, PageBreadcrumbs } from "@/shared/components";
import { EntityComments } from "@/shared/components/comments";
import { PageLayout } from "@/shared/components/PageLayout";
import { CollectionRecord } from "@/shared/domain/collections";
import { useLazyAPICall } from "@/shared/services/apiHooks";
import { APIResponse } from "@/types";

import { collectionEndpoint } from "../api";

import { CollectionDetails } from "./CollectionDetails";

/**
 * Details page for a single Collection.
 *
 * The collection to detail is determined from the page's URL.
 * The URL can contain the collection's ID.
 */
export const Collection: React.FC = () => {
  const { collectionId } = useParams<{ collectionId: string | undefined }>();
  const theme = useTheme();
  const { currentOrganization } = useAuth();

  const {
    data: response,
    error,
    loading,
    initiateRequest,
    updateCache,
  } = useLazyAPICall<APIResponse<CollectionRecord>>();

  // Note (2/2026): collection APIs don't utilize search endpoints to
  // retrieve a collection's files and datasets. Therefore, they don't
  // take advantage of query content modes, particularly 'RecordOnly'.
  // Currently, this results in collection files and datasets always being
  // fetched from the backend with their full metadata.
  React.useEffect(() => {
    async function fetchCollection() {
      if (!currentOrganization?.org_id || !collectionId) {
        return;
      }

      const queryParams = new URLSearchParams();
      queryParams.set("content_mode", "full");

      await initiateRequest({
        endpoint: collectionEndpoint,
        method: "GET",
        orgId: currentOrganization.org_id,
        pathParams: { collectionId: collectionId },
        queryParams,
      });
    }

    void fetchCollection();
  }, [currentOrganization?.org_id, initiateRequest, collectionId]);

  const breadcrumbs = [
    <Link key="list" to="/collections">
      Collections
    </Link>,
    <ExpandableCopyText
      key="collectionId"
      text={collectionId || ""}
      trimLength={15}
    />,
  ];

  let errorContent = null;
  let pageContent = null;
  if (error) {
    errorContent = (
      <Box
        sx={{
          mt: theme.spacing(2),
          mb: theme.spacing(1),
        }}
      >
        <Alert severity="error">{error.message}</Alert>
      </Box>
    );
  } else if (response?.data) {
    pageContent = (
      <CollectionDetails
        collection={response?.data}
        loading={loading}
        onUpdate={updateCache}
      />
    );
  }

  return (
    <PageLayout
      title={`Roboto - Collection - ${collectionId}`}
      headerContentLeft={<PageBreadcrumbs>{breadcrumbs}</PageBreadcrumbs>}
    >
      {errorContent}

      {pageContent}

      <Box
        sx={{
          paddingTop: theme.spacing(3),
        }}
      >
        {!loading && response && (
          <EntityComments
            entityType={"collection"}
            entityId={response?.data.collection_id}
          />
        )}
      </Box>
    </PageLayout>
  );
};
